import React, { useState, useEffect } from 'react';
import './App.css';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { Eye, Lock, Shield, Clock } from 'lucide-react';

function App() {
  const [currentPage, setCurrentPage] = useState('landing');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState('');
  const [viewData, setViewData] = useState([]);
  const [loading, setLoading] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    // Remove branding on page load and continuously monitor
    const removeBranding = () => {
      const emergentBadge = document.getElementById('emergent-badge');
      if (emergentBadge) {
        emergentBadge.remove();
      }
      
      // Remove any elements containing Emergent branding
      const brandingElements = document.querySelectorAll('*[id*="emergent"], *[class*="emergent"], a[href*="emergent"]');
      brandingElements.forEach(el => {
        if (el.textContent && el.textContent.toLowerCase().includes('emergent')) {
          el.remove();
        }
      });
    };

    // Remove on initial load
    removeBranding();
    
    // Monitor for dynamically added branding
    const observer = new MutationObserver(removeBranding);
    observer.observe(document.body, { childList: true, subtree: true });
    
    // Also run periodically as fallback
    const interval = setInterval(removeBranding, 1000);

    // Check if user is already authenticated
    const authToken = localStorage.getItem('authToken');
    const adminToken = localStorage.getItem('adminToken');
    
    if (authToken) {
      setIsAuthenticated(true);
      setCurrentPage('video');
    } else if (adminToken) {
      setIsAdmin(true);
      setCurrentPage('admin');
      fetchViewData();
    }

    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, []);

  // Add a utility function for making requests with timeout
  const fetchWithTimeout = async (url, options, timeout = 10000) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      console.log('🔍 Attempting login with backend URL:', BACKEND_URL);
      
      const response = await fetchWithTimeout(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      console.log('📡 Login response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.log('❌ Login failed:', errorData);
        setError(errorData.detail || 'Invalid credentials');
        setLoading(false);
        return;
      }

      const data = await response.json();
      console.log('✅ Login successful');
      
      localStorage.setItem('authToken', data.token);
      setIsAuthenticated(true);
      setCurrentPage('video');
      
      // Track video view with better error handling
      try {
        await fetchWithTimeout(`${BACKEND_URL}/api/views/track`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${data.token}`
          },
          body: JSON.stringify({ email }),
        });
        console.log('✅ View tracking successful');
      } catch (trackingError) {
        console.log('⚠️ View tracking failed, but continuing:', trackingError);
        // Don't fail the login if view tracking fails
      }
      
    } catch (err) {
      console.error('❌ Login connection error:', err);
      
      // Provide more specific error messages
      let errorMessage = 'Connection error. Please try again.';
      
      if (err.name === 'AbortError') {
        errorMessage = 'Request timed out. Please check your connection and try again.';
      } else if (err.name === 'TypeError' && err.message.includes('fetch')) {
        errorMessage = 'Unable to connect to server. Please check your internet connection and try again.';
      } else if (err.message.includes('NetworkError')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      setError(errorMessage);
    }
    
    setLoading(false);
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      console.log('🔍 Attempting admin login with backend URL:', BACKEND_URL);
      
      const response = await fetchWithTimeout(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: adminEmail, password: adminPassword }),
      });

      console.log('📡 Admin login response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.log('❌ Admin login failed:', errorData);
        setError(errorData.detail || 'Invalid admin credentials');
        setLoading(false);
        return;
      }

      const data = await response.json();
      console.log('✅ Admin login successful');

      localStorage.setItem('adminToken', data.token);
      setIsAdmin(true);
      setCurrentPage('admin');
      fetchViewData();
      
    } catch (err) {
      console.error('❌ Admin login connection error:', err);
      
      // Provide more specific error messages
      let errorMessage = 'Connection error. Please try again.';
      
      if (err.name === 'AbortError') {
        errorMessage = 'Request timed out. Please check your connection and try again.';
      } else if (err.name === 'TypeError' && err.message.includes('fetch')) {
        errorMessage = 'Unable to connect to server. Please check your internet connection and try again.';
      } else if (err.message.includes('NetworkError')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      setError(errorMessage);
    }
    
    setLoading(false);
  };

  const fetchViewData = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${BACKEND_URL}/api/admin/views`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
      });

      if (response.ok) {
        const data = await response.json();
        setViewData(data.views);
      }
    } catch (err) {
      console.error('Error fetching view data:', err);
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
    setIsAdmin(false);
    setCurrentPage('landing');
    setEmail('');
    setPassword('');
    setAdminEmail('');
    setAdminPassword('');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Landing Page
  if (currentPage === 'landing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width=%2260%22%20height=%2260%22%20viewBox=%220%200%2060%2060%22%20xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg%20fill=%22none%22%20fill-rule=%22evenodd%22%3E%3Cg%20fill=%22%23ffffff%22%20fill-opacity=%220.03%22%3E%3Ccircle%20cx=%2230%22%20cy=%2230%22%20r=%221%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-50"></div>
        
        <Card className="w-full max-w-md bg-gray-900/80 border-gray-800 backdrop-blur-xl">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <div>
              <CardTitle className="text-2xl font-bold text-white mb-2">
                Enter your email and the password you received to view this video.
              </CardTitle>
              <CardDescription className="text-gray-400">
                Access exclusive content from Jon Evans
              </CardDescription>
            </div>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-gray-800/50 border-gray-700 text-white placeholder-gray-400 focus:border-blue-500 h-12"
                />
              </div>
              
              <div className="space-y-2">
                <Input
                  type="password"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-gray-800/50 border-gray-700 text-white placeholder-gray-400 focus:border-blue-500 h-12"
                />
              </div>
              
              {error && (
                <div className="text-red-400 text-sm text-center bg-red-900/20 p-3 rounded-lg">
                  {error}
                </div>
              )}
              
              <Button 
                type="submit" 
                disabled={loading}
                className="w-full h-12 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-xl transition-all duration-300 transform hover:scale-105"
              >
                {loading ? 'Accessing...' : (
                  <>
                    <Eye className="w-5 h-5 mr-2" />
                    Watch Video
                  </>
                )}
              </Button>
            </form>
            
            <div className="mt-8 pt-6 border-t border-gray-800">
              <button
                onClick={() => setCurrentPage('adminLogin')}
                className="text-gray-500 hover:text-gray-300 text-sm transition-colors duration-200 flex items-center mx-auto"
              >
                <Shield className="w-4 h-4 mr-1" />
                Admin Access
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin Login Page
  if (currentPage === 'adminLogin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-gray-900 opacity-50"></div>
        
        <Card className="w-full max-w-md bg-gray-900/80 border-gray-800 backdrop-blur-xl">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-gradient-to-r from-red-500 to-orange-600 rounded-2xl flex items-center justify-center">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <div>
              <CardTitle className="text-2xl font-bold text-white mb-2">
                Admin Access
              </CardTitle>
              <CardDescription className="text-gray-400">
                View analytics and user data
              </CardDescription>
            </div>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleAdminLogin} className="space-y-4">
              <div className="space-y-2">
                <Input
                  type="email"
                  placeholder="Admin email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  required
                  className="bg-gray-800/50 border-gray-700 text-white placeholder-gray-400 focus:border-red-500 h-12"
                />
              </div>
              
              <div className="space-y-2">
                <Input
                  type="password"
                  placeholder="Admin password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  required
                  className="bg-gray-800/50 border-gray-700 text-white placeholder-gray-400 focus:border-red-500 h-12"
                />
              </div>
              
              {error && (
                <div className="text-red-400 text-sm text-center bg-red-900/20 p-3 rounded-lg">
                  {error}
                </div>
              )}
              
              <Button 
                type="submit" 
                disabled={loading}
                className="w-full h-12 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-semibold rounded-xl transition-all duration-300 transform hover:scale-105"
              >
                {loading ? 'Signing in...' : 'Admin Login'}
              </Button>
            </form>
            
            <div className="mt-6">
              <button
                onClick={() => setCurrentPage('landing')}
                className="text-gray-500 hover:text-gray-300 text-sm transition-colors duration-200 w-full text-center"
              >
                Back to Login
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Video Page
  if (currentPage === 'video' && isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black">
        <div className="absolute inset-0 bg-gray-900 opacity-30"></div>
        
        <div className="relative z-10 p-4 sm:p-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex justify-between items-center mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-white">
                A Message From Jon Evans - Founder of The Follow Up Ninja™
              </h1>
              <Button 
                onClick={logout}
                variant="outline"
                className="border-gray-600 text-gray-300 hover:text-white hover:border-gray-500"
              >
                Logout
              </Button>
            </div>
            
            <div className="bg-gray-900/60 backdrop-blur-lg rounded-2xl p-6 sm:p-8 border border-gray-800">
              <div className="aspect-video rounded-xl overflow-hidden mb-6">
                <script async src='https://cdn.eboov.com/campaign.js'></script>
                <div style={{padding:'56.25% 0 0 0',position:'relative'}}>
                  <iframe 
                    id='eboov-iframe' 
                    src='https://embed.eboov.com/v/?v=g69mpl6w' 
                    frameBorder='0' 
                    scrolling='no' 
                    allow='autoplay; fullscreen; picture-in-picture; encrypted-media' 
                    allowFullScreen={true}
                    style={{position:'absolute',top:0,left:0,width:'100%',height:'100%'}} 
                    title='tfun-buyer-message'
                  />
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 rounded-xl p-6 border border-blue-800/30">
                <h2 className="text-2xl font-bold text-white mb-4">Key Business Details:</h2>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-blue-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Potential for Rapid Scale - Steady Revenue</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-purple-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Proprietary Solution to the Most Common Problem Studio Owners Face</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-green-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Better than Captive Audience "In Love" with the Software</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  if (currentPage === 'admin' && isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black">
        <div className="absolute inset-0 bg-gray-900 opacity-30"></div>
        
        <div className="relative z-10 p-4 sm:p-8">
          <div className="max-w-6xl mx-auto">
            <div className="flex justify-between items-center mb-8">
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Admin Dashboard</h1>
                <p className="text-gray-400">Video Analytics & User Data</p>
              </div>
              <div className="flex gap-4">
                <Button 
                  onClick={fetchViewData}
                  variant="outline"
                  className="border-gray-600 text-gray-300 hover:text-white hover:border-gray-500"
                >
                  <Clock className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
                <Button 
                  onClick={logout}
                  variant="outline"
                  className="border-gray-600 text-gray-300 hover:text-white hover:border-gray-500"
                >
                  Logout
                </Button>
              </div>
            </div>
            
            <div className="bg-gray-900/60 backdrop-blur-lg rounded-2xl p-6 sm:p-8 border border-gray-800">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-white mb-2">Video Views</h2>
                <p className="text-gray-400">Total views: {viewData.length}</p>
              </div>
              
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-gray-700">
                      <TableHead className="text-gray-300">Email</TableHead>
                      <TableHead className="text-gray-300">First View</TableHead>
                      <TableHead className="text-gray-300">Last View</TableHead>
                      <TableHead className="text-gray-300">View Count</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {viewData.map((view, index) => (
                      <TableRow key={index} className="border-gray-700 hover:bg-gray-800/50">
                        <TableCell className="text-white font-medium">{view.email}</TableCell>
                        <TableCell className="text-gray-300">{formatDate(view.first_view)}</TableCell>
                        <TableCell className="text-gray-300">{formatDate(view.last_view)}</TableCell>
                        <TableCell className="text-gray-300">{view.view_count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                {viewData.length === 0 && (
                  <div className="text-center py-12 text-gray-400">
                    No video views recorded yet.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default App;